import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from './useAdminApi';

/**
 * Stripe payout → QBO deposit reconciliation console (superadmin).
 * Backs the manual-override panel on /admin/payments; the payout.paid webhook
 * does the same reconciliation automatically.
 */

export interface StripeDepositRow {
  stripe_payout_id: string;
  qbo_deposit_id: string | null;
  status: 'reconciling' | 'posted' | 'failed' | 'no_charges' | 'needs_review';
  gross: number | null;
  fees: number | null;
  net: number | null;
  charge_count: number | null;
  arrival_date: string | null;
  error: string | null;
  updated_at: string | null;
}

export interface StripePayoutRow {
  id: string;
  amount: number;
  currency: string;
  status: string;
  arrival_date: string;
  created: string;
  deposit: StripeDepositRow | null;
}

export interface StripePayoutsResponse {
  ok: true;
  stripe_configured: boolean;
  auto_deposit_enabled?: boolean;
  payouts: StripePayoutRow[];
}

export function useStripePayouts(enabled: boolean) {
  return useQuery<StripePayoutsResponse>({
    queryKey: ['stripe-payouts'],
    queryFn: () => adminFetch('admin-stripe-payouts'),
    enabled,
    staleTime: 30 * 1000,
  });
}

export function useReconcilePayout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payoutId: string) =>
      adminFetch('admin-stripe-payouts', { method: 'POST', body: JSON.stringify({ action: 'reconcile', payout_id: payoutId }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['stripe-payouts'] }),
  });
}

export function useSweepPayouts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (days?: number) =>
      adminFetch('admin-stripe-payouts', { method: 'POST', body: JSON.stringify({ action: 'sweep', days: days ?? 14 }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['stripe-payouts'] }),
  });
}

export interface StripeWebhookStatus {
  ok: true;
  stripe_configured: boolean;
  url?: string;
  endpoint?: { id: string; status: string; enabled_events: string[]; missing_events: string[] } | null;
  desired_events?: string[];
}

export function useStripeWebhookStatus(enabled: boolean) {
  return useQuery<StripeWebhookStatus>({
    queryKey: ['stripe-webhook-status'],
    queryFn: () => adminFetch('admin-stripe-webhook-setup'),
    enabled,
    staleTime: 60 * 1000,
  });
}

export interface EnsureWebhookResult {
  ok: true;
  status: 'created' | 'updated' | 'already_complete';
  endpoint_id: string;
  url: string;
  enabled_events: string[];
  added_events?: string[];
  signing_secret?: string | null;
  note?: string;
}

export function useEnsureStripeWebhook() {
  const qc = useQueryClient();
  return useMutation<EnsureWebhookResult>({
    mutationFn: () => adminFetch('admin-stripe-webhook-setup', { method: 'POST', body: JSON.stringify({ action: 'ensure' }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['stripe-webhook-status'] }),
  });
}

export interface PayoutDetailLine {
  kind: 'charge' | 'account_fee' | 'other';
  description: string;
  customer: string | null;
  qbo_payment_id: string | null;
  payment_intent: string | null;
  gross: number;
  fee: number;
  net: number;
  created: string;
}

export interface PayoutDetail {
  ok: true;
  payout_id: string;
  lines: PayoutDetailLine[];
  totals: { gross: number; deducted: number; net: number };
}

/** What is inside one payout. Only fetched when its row is opened. */
export function usePayoutDetail(payoutId: string | null) {
  return useQuery<PayoutDetail>({
    queryKey: ['stripe-payout-detail', payoutId],
    queryFn: () => adminFetch(`admin-stripe-payouts?payout_id=${encodeURIComponent(payoutId!)}`),
    enabled: !!payoutId,
    staleTime: 60 * 1000,
  });
}
